/* FLM — shared voice-intro recorder card, extracted from the wizard's VoiceCard
   (onboarding.jsx StepPresence) so the wizard and the profile edit sheet share
   ONE state machine: idle (record) → rec (stop + elapsed + live wave) → done
   (play/pause + duration + remove). Hardened for Telegram WebViews:
   - mimeType via MediaRecorder.isTypeSupported fallback chain
     (webm;codecs=opus → webm → mp4 → '' — iOS/Android WebViews report mp4/empty)
   - the blob keeps the recorder's REAL mimeType (never fabricates audio/webm
     over mp4 bytes; empty type lets the server sniff the container)
   - sub-second / empty takes are discarded
   - a FAILED upload shows an inline error (photo upErr pattern) with the take
     preserved and a retry button — never a silent loss
   - `initial` ({ url, duration }) seeds the done state from a server-saved
     intro (profile edit); remove deletes it server-side, re-record replaces. */
import React from 'react';
import { Icon, VoiceWave } from './ui';
import { isDemo } from '../lib/demo';
import { uploadVoice, deleteVoice } from '../api/client';

const CARD = {
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 16,
  boxShadow: 'var(--shadow-card)',
  position: 'relative',
};
const ROW = { display: 'flex', alignItems: 'center' };
const COL = { display: 'flex', flexDirection: 'column' };

const MAX_SECS = 12; // server accepts 1–12s voice intros
/* Telegram iOS WebView: mp4 only; some Android WebViews: webm without the
   codecs param; '' lets the engine pick its own default container. */
const MIME_CHAIN = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];

function pickMime() {
  try {
    for (const m of MIME_CHAIN) if (MediaRecorder.isTypeSupported(m)) return m;
  } catch { /* some webviews throw on isTypeSupported — fall through */ }
  return '';
}

export function VoiceRecorder({ t, faNum, initial = null, note = null, onUploaded, onDeleted, style = {} }) {
  /* a server-saved intro (profile edit) seeds the recorded state directly */
  const hasInitial = Boolean(initial && initial.url);
  const [state, setState] = React.useState(hasInitial ? 'done' : 'idle'); // idle | rec | done
  const [secs, setSecs] = React.useState(0);
  const [dur, setDur] = React.useState(hasInitial ? initial.duration || 1 : 0);
  const [playing, setPlaying] = React.useState(false);
  const [micErr, setMicErr] = React.useState(false);
  const [upErr, setUpErr] = React.useState(false); // upload failed — take kept, retry offered
  const timer = React.useRef(null);
  const cap = React.useRef(null);
  const recorder = React.useRef(null);
  const chunks = React.useRef([]);
  const startedAt = React.useRef(0);
  const audio = React.useRef(null);
  const takeUrl = React.useRef(null);
  const take = React.useRef(null); // { blob, secs } — preserved across failed uploads for retry
  const dead = React.useRef(false); // unmounted mid-recording → drop the take

  /* Server intro playback (profile edit). Rebuilds when the saved URL changes
     (e.g. after an upload + getMe refetch hands the sheet a fresh voiceUrl) so
     the play button never holds a stale/absent element. A locally recorded take
     always wins — its blob Audio (set in onstop) is what should play. onerror
     resets the button just like the deck's playback (discover.jsx). */
  const initialUrl = hasInitial ? initial.url : null;
  React.useEffect(() => {
    if (!initialUrl || take.current) return; // no server intro, or a local take owns playback
    const a = new Audio(initialUrl);
    a.onended = () => setPlaying(false);
    a.onerror = () => setPlaying(false);
    audio.current = a;
  }, [initialUrl]);

  /** Upload the kept take; failure shows the inline note + retry (take stays). */
  const doUpload = () => {
    const tk = take.current;
    if (!tk) return;
    setUpErr(false);
    uploadVoice(tk.blob, tk.secs)
      .then((res) => {
        if (dead.current) return;
        if (onUploaded) onUploaded(res);
      })
      .catch(() => {
        if (dead.current || isDemo()) return; // demo design review keeps the local take quiet
        setUpErr(true);
      });
  };

  const start = () => {
    if (state !== 'idle') return;
    setMicErr(false);
    const md = typeof navigator !== 'undefined' ? navigator.mediaDevices : null;
    if (!md || !md.getUserMedia || typeof MediaRecorder === 'undefined') { setMicErr(true); return; }
    md.getUserMedia({ audio: true }).then((stream) => {
      if (dead.current) { stream.getTracks().forEach((tr) => tr.stop()); return; } // granted after unmount
      const mime = pickMime();
      let r;
      try { r = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream); }
      catch { stream.getTracks().forEach((tr) => tr.stop()); setMicErr(true); return; }
      chunks.current = [];
      r.ondataavailable = (e) => { if (e.data && e.data.size) chunks.current.push(e.data); };
      r.onstop = () => {
        stream.getTracks().forEach((tr) => tr.stop());
        clearInterval(timer.current);
        clearTimeout(cap.current);
        recorder.current = null;
        if (dead.current) return; // left the screen mid-take — never upload unreviewed audio
        const elapsed = Math.min(MAX_SECS, Math.round((Date.now() - startedAt.current) / 1000));
        /* keep the recorder's REAL container type; '' lets the server sniff
           the magic bytes instead of trusting a fabricated audio/webm */
        const type = r.mimeType || mime || '';
        const blob = type ? new Blob(chunks.current, { type }) : new Blob(chunks.current);
        if (blob.size > 0 && elapsed >= 1) {
          const url = URL.createObjectURL(blob);
          takeUrl.current = url;
          const a = new Audio(url);
          a.onended = () => setPlaying(false);
          a.onerror = () => setPlaying(false);
          audio.current = a;
          take.current = { blob, secs: elapsed };
          setDur(elapsed);
          setState('done');
          doUpload();
        } else {
          setState('idle'); // sub-second / empty take → nothing to review
        }
      };
      recorder.current = r;
      startedAt.current = Date.now();
      r.start();
      setSecs(0);
      setState('rec'); // timer starts only once the mic is actually live
      timer.current = setInterval(() => setSecs((s) => Math.min(s + 0.1, MAX_SECS)), 100);
      cap.current = setTimeout(stop, MAX_SECS * 1000);
    }).catch(() => setMicErr(true)); // mic denied → quiet note, stays idle
  };
  const stop = () => {
    clearInterval(timer.current);
    clearTimeout(cap.current);
    const r = recorder.current;
    if (!r) { setState((s) => (s === 'rec' ? 'idle' : s)); return; }
    try { if (r.state !== 'inactive') r.stop(); /* onstop drives the transition */ }
    catch { recorder.current = null; setState('idle'); }
  };
  /** Remove the take: discard locally + delete the server copy (404 is fine). */
  const discard = () => {
    if (audio.current) { try { audio.current.pause(); } catch { /* noop */ } audio.current = null; }
    if (takeUrl.current) { URL.revokeObjectURL(takeUrl.current); takeUrl.current = null; }
    take.current = null;
    setPlaying(false);
    setUpErr(false);
    setDur(0);
    setSecs(0);
    setState('idle'); // re-record from scratch
    deleteVoice().catch(() => {});
    if (onDeleted) onDeleted();
  };
  const togglePlay = () => {
    const a = audio.current;
    if (!a) return;
    if (playing) { a.pause(); setPlaying(false); }
    else a.play().then(() => setPlaying(true)).catch(() => {});
  };
  React.useEffect(() => {
    // A live mount is never dead — reset the flag the cleanup sets. Without this,
    // React.StrictMode's mount→cleanup→mount (and any remount) leaves dead=true,
    // so onstop/doUpload bail and a freshly recorded take is silently dropped
    // (the "recording a new intro doesn't save" bug).
    dead.current = false;
    return () => {
      dead.current = true;
      clearInterval(timer.current);
      clearTimeout(cap.current);
      try { if (recorder.current && recorder.current.state !== 'inactive') recorder.current.stop(); } catch { /* noop */ }
      if (audio.current) { try { audio.current.pause(); } catch { /* noop */ } }
      if (takeUrl.current) URL.revokeObjectURL(takeUrl.current);
    };
  }, []);
  const clock = (s) => faNum(`0:${String(Math.floor(s)).padStart(2, '0')}`);

  return (
    <div className="flm-hairline" style={{ ...CARD, ...COL, padding: 16, gap: 12, ...style }}>
      <div style={{ ...ROW, justifyContent: 'space-between' }}>
        <span className="flm-label">{t('voiceIntro')}</span>
        <span className="flm-mono" style={{ fontSize: 9, color: state === 'rec' ? 'var(--love)' : state === 'done' ? 'var(--accent)' : 'var(--text-3)' }}>
          {state === 'rec' ? `● ${t('recording')}` : state === 'done' ? t('soundsGreat') : t('obOptional')}
        </span>
      </div>

      {state === 'idle' ? (
        /* Idle — single record row */
        <button className="flm-ghost-btn" onClick={start} style={{
          ...ROW, justifyContent: 'center', gap: 9, height: 46, width: '100%', borderRadius: 12,
          border: '1px solid rgba(255,212,145,0.45)', background: 'var(--accent-tint)',
          color: 'var(--accent)', fontSize: 14.5, fontWeight: 600,
        }}>
          <Icon name="mic" size={17} />
          {t('obRecordVoice')}
        </button>
      ) : null}

      {state === 'rec' ? (
        /* Recording — stop + elapsed + live wave */
        <div style={{ ...ROW, gap: 14 }}>
          <button className="flm-ghost-btn" onClick={stop} aria-label={t('obStopRec')} style={{
            width: 44, height: 44, borderRadius: '50%', padding: 0, flex: 'none',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'var(--love)', boxShadow: 'var(--glow-love)',
          }}>
            <span style={{ width: 13, height: 13, borderRadius: 3, background: '#fff' }}></span>
          </button>
          <div style={{ flex: 1 }}><VoiceWave playing bars={26} height={22} color="var(--accent)" /></div>
          <span className="flm-mono" style={{ fontSize: 10 }}>{clock(secs)}</span>
        </div>
      ) : null}

      {state === 'done' ? (
        /* Recorded / saved — play/pause + duration + remove */
        <div style={{ ...ROW, gap: 14 }}>
          <button className="flm-ghost-btn" onClick={togglePlay} style={{
            width: 44, height: 44, borderRadius: '50%', padding: 0, flex: 'none',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'var(--accent)', boxShadow: 'var(--shadow-cta)',
          }}>
            <Icon name={playing ? 'pause' : 'play'} size={17} color="none" fill="#000"
              style={{ marginInlineStart: playing ? 0 : 2 }} />
          </button>
          <div style={{ flex: 1 }}><VoiceWave playing={playing} bars={22} height={22} color="var(--accent)" /></div>
          <span className="flm-mono" style={{ fontSize: 10 }}>{clock(dur)}</span>
          <button className="flm-ghost-btn" onClick={discard} aria-label="Remove voice intro" style={{
            width: 34, height: 34, borderRadius: '50%', padding: 0, flex: 'none',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '1px solid var(--border-bright)', color: 'var(--text-3)',
          }}>
            <Icon name="x" size={15} />
          </button>
        </div>
      ) : null}

      {upErr ? (
        /* upload failed — the take is preserved above; retry re-sends it */
        <div style={{ ...ROW, gap: 8 }}>
          <Icon name="x" size={14} color="var(--love)" />
          <span style={{ fontSize: 12.5, color: 'var(--love)', flex: 1 }}>{t('voiceUploadErr')}</span>
          <button className="flm-ghost-btn" onClick={doUpload} style={{
            color: 'var(--accent)', fontSize: 12.5, fontWeight: 600, padding: '2px 4px', flex: 'none',
          }}>{t('retry')}</button>
        </div>
      ) : null}

      {note ? <span style={{ fontSize: 12.5, color: 'var(--text-3)' }}>{note}</span> : null}
      {micErr ? <span className="flm-mono" style={{ fontSize: 9, color: 'var(--text-3)' }}>{t('micUnavailable')}</span> : null}
    </div>
  );
}
